import { Box, FormControl, InputLabel, MenuItem, Select, Snackbar, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TemplateFieldsSection from '../components/common/TemplateFieldsSection/TemplateFieldsSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import { DisableContext } from '../contexts/DisableContextProvider';
import { useCreateFormTemplate, useGetFormTemplateById, useUpdateFormTemplate } from '../services/react-query/form_templates';
import { inputContainerStyling, inputDividerStyling } from '../styles/sxStyling';
import { FormTemplate, FormTemplateField } from '../types/FormTemplate';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator, uuidFinder } from '../utility/inputUtils';
import TagsSection from '../components/common/TagsSection/TagsSection';
import AccordionSection from '../components/AccordionSection';
import { FieldType, TemplateField } from '../types/FieldCommon';
import _ from 'lodash';
import AttachmentsSection from '../components/attachments/AttachmentsSection';
import { AttachmentOwnerType } from '../types/Attachment';
import useStateWithDirty from '../hooks/useStateWithDirty';
import { useGetAllEmployees } from '../services/react-query/employees/useGetAllEmployees';
import EmployeeSearchAutocomplete from '../components/common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';

export default function FormTemplateItem() {
  const { uuid } = useParams();
  const { state: formTemplate, setState: setFormTemplate, resetState: resetFormTemplate, isDirty } = useStateWithDirty<FormTemplate | null>(null);
  const [saved, setSaved] = useState<boolean>(false);

  const { data, isLoading } = useGetFormTemplateById({ uuid });

  const { mutate: createFormTemplate, isPending: isCreating } = useCreateFormTemplate({ onSuccessCallback: () => null });
  const { mutate: updateFormTemplate, isPending: isUpdating } = useUpdateFormTemplate({
    uuid,
    onSuccessCallback: () => setSaved(true),
  });

  const employeeFilters = {
    form_template: uuid ?? '',
  };
  const { data: employeeData } = useGetAllEmployees({ params: { per_page: 0, filters: employeeFilters }, enabled: !!uuid });
  const employees = employeeData?.data ?? [];

  useEffect(() => {
    if (data) resetFormTemplate(data);
  }, [data]);

  const { disabled } = useContext(DisableContext);

  if (isLoading || formTemplate === null) return <Loading />;

  const handleSave = () => {
    if (uuid === 'new') {
      createFormTemplate(formTemplate);
    } else {
      updateFormTemplate(formTemplate);
    }
  };

  // TODO add handling for required and description values
  const setFields = (templateFields: FormTemplateField[]) => setFormTemplate({ ...formTemplate, fields: _.map(templateFields, (field) => ({ ...field })) });

  const createField = (): FormTemplateField => ({
    label: '',
    type: FieldType.STRING,
    uuid: crypto.randomUUID(),
    required: false,
    description: '',
  });

  return (
    <Box>
      <ItemHeader
        title={`${uuid === 'new' ? 'Create' : 'Update'} Form Template`}
        onSave={handleSave}
        isLoading={isCreating || isUpdating}
        disableActions={!isDirty}
      />
      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          <Box sx={inputDividerStyling}>
            <TextField
              id="name"
              label="Name"
              autoFocus
              onChange={(event) => setFormTemplate({ ...formTemplate, name: event.target.value })}
              value={formTemplate.name}
              disabled={disabled}
              {...errorPopulator(formTemplate, 'name')}
            />
 
            <TextField
              id="instructions"
              label="Instructions"
              multiline
              minRows={3}
              onChange={(event) => setFormTemplate({ ...formTemplate, instructions: event.target.value })}
              value={formTemplate.instructions ?? ''}
              disabled={disabled}
            />
          </Box>

          <Box sx={inputDividerStyling}>
            <TextField
              id="description"
              label="Description"
              onChange={(event) => setFormTemplate({ ...formTemplate, description: event.target.value })}
              value={formTemplate.description}
              disabled={disabled}
              {...errorPopulator(formTemplate, 'description')}
            />

            <FormControl fullWidth>
              <InputLabel id="attachment-requirement">Attachment Requirement</InputLabel>
              <Select
                labelId="attachment-requirement"
                label="Attachment Requirement"
                value={formTemplate.attachment_requirement}
                onChange={(event) => setFormTemplate({ ...formTemplate, attachment_requirement: event.target.value as FormTemplate['attachment_requirement'] })}
                disabled={disabled}
              >
                <MenuItem value="na">Not Allowed</MenuItem>
                <MenuItem value="allowed">Allowed</MenuItem>
                <MenuItem value="required">Required</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={inputDividerStyling}>
            <EmployeeSearchAutocomplete
              isDisabled={disabled}
              label="Employee to Notify"
              value={uuidFinder(employees, formTemplate.employee_uuid)}
              onChange={(employee) => {
                setFormTemplate((prev) => (prev ? { ...prev, employee_uuid: employee?.uuid, employee_display_name: employee?.display_name } : prev));
              }}
              error={errorPopulator(formTemplate, 'employee')}
            />
          </Box>
        </Box>
      </AccordionSection>

      <TemplateFieldsSection
        fields={formTemplate.fields}
        setFields={setFields}
        createField={createField}
        isForm={true}
        collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_FIELDS}
      />

      <TagsSection
        tags={formTemplate.tags}
        collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_TAGS}
        setTags={(tags) => setFormTemplate({ ...formTemplate, tags })}
      />

      <AttachmentsSection
        collapseId={UserSettingsCollapseKeys.FORM_TEMPLATES_ITEM_ATTACHMENTS}
        ownerType={AttachmentOwnerType.FORM_TEMPLATE}
        ownerUuid={uuid ?? ''}
      />

      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Box>
  );
}
